import { FlaskConical, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';

interface TestResults {
  testRunId?: string;
  testRunStatus?: string;
  totalScenarios?: number;
  passed?: number;
  failed?: number;
  skipped?: number;
  unmapped?: number;
  coveragePercent?: number;
  summary?: Record<string, unknown>;
}

interface Props {
  testResults: TestResults | null | undefined;
}

function ScenarioDonut({ passed, failed, skipped, unmapped, total }: {
  passed: number; failed: number; skipped: number; unmapped: number; total: number;
}) {
  if (total === 0) return null;

  // Build arc segments for a simple horizontal stacked bar (more readable than a donut at this size)
  const segments = [
    { count: passed, color: 'bg-emerald-500', label: 'Passed' },
    { count: failed, color: 'bg-red-500', label: 'Failed' },
    { count: skipped, color: 'bg-neutral-300', label: 'Skipped' },
    { count: unmapped, color: 'bg-amber-400', label: 'Unmapped' },
  ].filter(s => s.count > 0);

  return (
    <div className="space-y-1.5">
      {/* Stacked bar */}
      <div className="h-2.5 w-full rounded-full bg-neutral-100 overflow-hidden flex">
        {segments.map((s) => (
          <div
            key={s.label}
            className={`h-full ${s.color} transition-all duration-500`}
            style={{ width: `${(s.count / total) * 100}%` }}
            title={`${s.label}: ${s.count}`}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {segments.map((s) => (
          <div key={s.label} className="flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full ${s.color}`} />
            <span className="text-[9px] text-neutral-500">
              {s.label} <span className="font-mono font-semibold text-neutral-600 tabular-nums">{s.count}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TestEvidenceCard({ testResults }: Props) {
  if (!testResults || !testResults.totalScenarios) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-6 text-center">
        <FlaskConical size={20} className="mx-auto text-neutral-300 mb-2" />
        <p className="text-[11px] text-neutral-400">No BDD test evidence yet</p>
      </div>
    );
  }

  const { totalScenarios = 0, passed = 0, failed = 0, skipped = 0, unmapped = 0, coveragePercent = 0, testRunStatus } = testResults;

  const statusColor = testRunStatus === 'completed'
    ? 'bg-emerald-100 text-emerald-700'
    : testRunStatus === 'failed'
    ? 'bg-red-100 text-red-700'
    : 'bg-neutral-100 text-neutral-600';

  const coverageColor = coveragePercent >= 80
    ? 'text-emerald-600'
    : coveragePercent >= 50
    ? 'text-amber-600'
    : coveragePercent > 0
    ? 'text-red-600'
    : 'text-neutral-400';

  return (
    <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-100 flex items-center justify-between">
        <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">BDD Evidence</p>
        {testRunStatus && (
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${statusColor}`}>
            {testRunStatus}
          </span>
        )}
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* Coverage headline */}
        <div className="flex items-baseline gap-2">
          <span className={`text-2xl font-bold font-mono tabular-nums ${coverageColor}`}>
            {coveragePercent}%
          </span>
          <span className="text-[11px] text-neutral-400">coverage</span>
        </div>

        {/* Scenario counts */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <CheckCircle2 size={11} className="text-emerald-500" />
            <span className="text-[10px] font-mono text-neutral-600 tabular-nums">{passed}</span>
          </div>
          <div className="flex items-center gap-1">
            <XCircle size={11} className="text-red-500" />
            <span className="text-[10px] font-mono text-neutral-600 tabular-nums">{failed}</span>
          </div>
          {skipped > 0 && (
            <div className="flex items-center gap-1">
              <MinusCircle size={11} className="text-neutral-400" />
              <span className="text-[10px] font-mono text-neutral-600 tabular-nums">{skipped}</span>
            </div>
          )}
          <span className="text-[10px] text-neutral-400 ml-auto">
            {totalScenarios} scenarios
          </span>
        </div>

        {/* Stacked bar */}
        <ScenarioDonut
          passed={passed}
          failed={failed}
          skipped={skipped}
          unmapped={unmapped}
          total={totalScenarios}
        />
      </div>
    </div>
  );
}
